import { useCallback, useEffect, useRef, useState } from 'react';
import { operatorConsole } from '../../lib/api/operatorConsole';

export type DesignSystem = Record<string, unknown>;

// The template's design_system (null while it loads). `onLoaded` runs in the
// same tick the theme lands, so what it dispatches renders together with it.
export function useTemplateTheme(template: string, onLoaded: (designSystem: DesignSystem) => void) {
  const [theme, setTheme] = useState<{ template: string; designSystem: DesignSystem } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const onLoadedRef = useRef(onLoaded);

  useEffect(() => {
    onLoadedRef.current = onLoaded;
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fresh = await operatorConsole.getTemplateTheme(template);
        if (cancelled) return;
        setTheme({ template, designSystem: fresh.design_system });
        onLoadedRef.current(fresh.design_system);
        setLoadError(null);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Failed to load theme');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [template]);

  const replaceDesignSystem = useCallback(
    (designSystem: DesignSystem) => setTheme({ template, designSystem }),
    [template],
  );

  // The previous template's theme is still loading out, not this one's
  const designSystem = theme?.template === template ? theme.designSystem : null;
  return { designSystem, replaceDesignSystem, loadError };
}
